import { permissionKey } from "@/lib/rbacCatalog";
import { hasScopeOrImplied } from "@/lib/scopeHierarchy";

/** Cognito resource server identifier (must match pool + Lambda).
 *  Read from window.__ENV__ (runtime config.js) so each env gets its own
 *  identifier without a rebuild. Falls back to process.env then halo-dev1-api.
 */
export const RESOURCE_SERVER_ID = (
  window.__ENV__?.REACT_APP_COGNITO_RESOURCE_SERVER ||
  process.env.REACT_APP_COGNITO_RESOURCE_SERVER ||
  "halo-dev1-api"
).trim();

/**
 * Catalog for FE gating + hierarchy implication.
 * Keep product scopes in sync with sprint-6/RBAC/catalogs/rbac_scopes.json.
 * Extra verbs (e.g. tickets.delete) may remain for implication / baseline JWT compat.
 */
export const ALL_SCOPE_NAMES = [
  "dashboard.read",
  "session.read",
  "analytics.read",
  "reports.read",
  "tickets.read",
  "tickets.create",
  "tickets.update",
  "tickets.delete",
  "discovery.read",
    "approvals.read",
    "approvals.update",
    "ffid.update",
    "trigger-workflow.read",
    "trigger-workflow.create",
  "errors.read",
  "errors.update",
  "deployment.read",
  "deployment.create",
  "deployment.update",
  "business-value.read",
  "business-value.create",
  "business-value.update",
  "business-value.delete",
  "agent-studio.read",
  "agent-studio.create",
  "agent-studio.update",
  "agent-studio.delete",
  "vibe-studio.read",
  "vibe-studio.create",
  "vibe-studio.update",
  "settings.sap-landscape.read",
  "settings.sap-landscape.create",
  "settings.sap-landscape.update",
  "settings.sap-landscape.delete",
  "settings.mcp-servers.read",
  "settings.mcp-servers.create",
  "settings.mcp-servers.update",
  "settings.mcp-servers.delete",
  "settings.general.read",
  "settings.general.update",
  "settings.user-management.read",
  "settings.user-management.create",
  "settings.user-management.update",
  "settings.group-management.read",
  "settings.group-management.create",
  "settings.group-management.update",
  "settings.group-management.delete",
];

/** Known Halo RBAC scope short names (resource-server scopes only). */
export const HALO_SCOPE_NAMES = new Set(ALL_SCOPE_NAMES);

/**
 * Catalog "write" actions map to any mutation scope (create, update, or delete).
 * Use for coarse "can mutate at all" checks only — never for canUpdate/canDelete
 * (create-only packs would incorrectly unlock update/delete UI).
 * @type {Record<string, string[]>}
 */
const WRITE_MUTATION_SCOPES = {
  [permissionKey("agent-studio", "write")]: [
    "agent-studio.create",
    "agent-studio.update",
    "agent-studio.delete",
  ],
  [permissionKey("vibe-studio", "write")]: [
    "vibe-studio.create",
    "vibe-studio.update",
  ],
  [permissionKey("deployment", "write")]: [
    "deployment.create",
    "deployment.update",
  ],
  [permissionKey("errors", "write")]: ["errors.update"],
  [permissionKey("business-value", "write")]: [
    "business-value.create",
    "business-value.update",
    "business-value.delete",
  ],
  [permissionKey("settings.sap-landscape", "write")]: [
    "settings.sap-landscape.create",
    "settings.sap-landscape.update",
    "settings.sap-landscape.delete",
  ],
  [permissionKey("settings.mcp-servers", "write")]: [
    "settings.mcp-servers.create",
    "settings.mcp-servers.update",
    "settings.mcp-servers.delete",
  ],
  [permissionKey("settings.general", "write")]: ["settings.general.update"],
  [permissionKey("settings.user-management", "write")]: [
    "settings.user-management.create",
    "settings.user-management.update",
  ],
  [permissionKey("settings.group-management", "write")]: [
    "settings.group-management.create",
    "settings.group-management.update",
    "settings.group-management.delete",
  ],
  [permissionKey("approvals", "write")]: ["approvals.update"],
  [permissionKey("tickets", "write")]: ["tickets.create", "tickets.update", "tickets.delete"],
};

/**
 * Legacy permission keys → Cognito scope short names.
 * null = no scope (deny UI access).
 * @type {Record<string, string | null>}
 */
export const PERMISSION_SCOPE_MAP = {
  [permissionKey("dashboard", "read")]: "dashboard.read",
  [permissionKey("tickets", "read")]: "tickets.read",
  [permissionKey("tickets", "create")]: "tickets.create",
  [permissionKey("tickets", "update")]: "tickets.update",
  [permissionKey("trigger-workflow", "read")]: "trigger-workflow.read",
  [permissionKey("trigger-workflow", "trigger")]: "trigger-workflow.create",
  [permissionKey("agent-studio", "read")]: "agent-studio.read",
  [permissionKey("vibe-studio", "read")]: "vibe-studio.read",
  [permissionKey("deployment", "read")]: "deployment.read",
  [permissionKey("errors", "read")]: "errors.read",
  [permissionKey("analytics", "read")]: "analytics.read",
  [permissionKey("business-value", "read")]: "business-value.read",
  [permissionKey("reports", "read")]: "reports.read",
  [permissionKey("documentation", "read")]: "reports.read",
  [permissionKey("discovery", "read")]: "discovery.read",
  [permissionKey("approvals", "read")]: "approvals.read",
  [permissionKey("ffid", "update")]: "ffid.update",
  [permissionKey("settings.sap-landscape", "read")]: "settings.sap-landscape.read",
  [permissionKey("settings.mcp-servers", "read")]: "settings.mcp-servers.read",
  [permissionKey("settings.general", "read")]: "settings.general.read",
  [permissionKey("settings.general", "write")]: "settings.general.update",
  [permissionKey("settings.notifications", "read")]: null,
  [permissionKey("settings.notifications", "write")]: null,
  [permissionKey("settings.user-management", "read")]: "settings.user-management.read",
  [permissionKey("settings.user-management", "write")]: "settings.user-management.update",
  [permissionKey("settings.group-management", "read")]: "settings.group-management.read",
  [permissionKey("settings.group-management", "write")]: "settings.group-management.update",
  [permissionKey("admin.users", "read")]: "settings.user-management.read",
  [permissionKey("admin.users", "create")]: "settings.user-management.create",
  [permissionKey("admin.users", "update")]: "settings.user-management.update",
  [permissionKey("admin.users", "delete")]: "settings.user-management.update",
  [permissionKey("admin.groups", "read")]: "settings.group-management.read",
  [permissionKey("admin.groups", "create")]: "settings.group-management.create",
  [permissionKey("admin.groups", "update")]: "settings.group-management.update",
  [permissionKey("admin.groups", "delete")]: "settings.group-management.delete",
};

export function fullScopeName(shortName) {
  return `${RESOURCE_SERVER_ID}/${shortName}`;
}

export function permissionToScopeName(resource, action) {
  const key = permissionKey(resource, action);
  if (key in PERMISSION_SCOPE_MAP) {
    return PERMISSION_SCOPE_MAP[key];
  }
  return `${resource}.${action}`;
}

export function isHaloScopeName(shortName) {
  return HALO_SCOPE_NAMES.has(shortName);
}

/** True when the token includes at least one Halo resource-server scope. */
export function hasAnyHaloScope(scopeSet) {
  for (const name of scopeSet) {
    if (HALO_SCOPE_NAMES.has(name)) return true;
  }
  return false;
}

/**
 * @param {string | undefined} scopeClaim — space-separated scopes from access JWT
 * @returns {Set<string>} short scope names (e.g. tickets.read)
 */
export function parseScopeClaim(scopeClaim) {
  if (!scopeClaim || typeof scopeClaim !== "string") {
    return new Set();
  }
  const prefix = `${RESOURCE_SERVER_ID}/`;
  const out = new Set();
  for (const part of scopeClaim.trim().split(/\s+/)) {
    if (!part) continue;
    if (part.startsWith(prefix)) {
      const shortName = part.slice(prefix.length);
      if (HALO_SCOPE_NAMES.has(shortName)) {
        out.add(shortName);
      }
    } else if (part.includes("/")) {
      const shortName = part.split("/").pop();
      if (shortName && HALO_SCOPE_NAMES.has(shortName)) {
        out.add(shortName);
      }
    } else if (HALO_SCOPE_NAMES.has(part)) {
      out.add(part);
    }
  }
  return out;
}

/**
 * @param {string | null | undefined} jwt — Cognito access token
 * @returns {Set<string>}
 */
export function parseScopesFromAccessToken(jwt) {
  if (!jwt) return new Set();
  try {
    const payload = JSON.parse(
      atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    if (payload.token_use && payload.token_use !== "access") {
      return new Set();
    }
    return parseScopeClaim(payload.scope || "");
  } catch {
    return new Set();
  }
}

export function hasScope(scopeSet, shortName) {
  if (!shortName) return false;
  return scopeSet.has(shortName);
}

export function hasAnyScope(scopeSet, shortNames) {
  return shortNames.some((s) => scopeSet.has(s));
}

/**
 * Read permissions use phase-1 hierarchy (create|update|delete ⇒ read).
 * Mutations / write maps still require an exact matching scope.
 */
export function hasScopeForPermission(scopeSet, resource, action) {
  const key = permissionKey(resource, action);
  const mutationScopes = WRITE_MUTATION_SCOPES[key];
  if (mutationScopes) {
    return mutationScopes.some((name) => hasScope(scopeSet, name));
  }
  const mapped = permissionToScopeName(resource, action);
  if (mapped === null) return false;
  return hasScopeOrImplied(scopeSet, mapped, ALL_SCOPE_NAMES);
}
